import React from 'react';
import { Link } from 'react-router-dom';
import Footer from './components/Footer';

/**
 * Terms and Conditions, including the POPIA privacy notice and the donation
 * and refund terms.
 *
 * This is a plain-language starting point, not legal advice -- it should be
 * reviewed by someone qualified before it is relied on.
 *
 * On scope, since it is easy to get backwards: ECTA s43 prescribes supplier
 * disclosures, but it opens with "A supplier offering goods or services for
 * sale, for hire or for exchange by way of an electronic transaction". A
 * donation is none of those -- the donor receives nothing in return -- so s43
 * most likely does not bind this page.
 *
 * The physical address s43(1)(b) and (g) call for is therefore deliberately
 * absent, not an oversight. If the site ever sells anything -- merchandise,
 * tickets, training -- s43 applies and it has to go back in, along with a
 * delivery period and a returns policy.
 *
 * POPIA (section 6) is different and does apply: it governs the processing of
 * personal information, regardless of whether anything is sold.
 */

const LAST_UPDATED = '23 October 2026';

const Terms: React.FC = () => (
    <div className="font-sans text-gray-800">

        {/* Hero Banner */}
        <section id="banner" className="hero-banner">
            <img src="/media/hero-banner.jpg" alt="Kilo Delta On Call" className="hero-banner-image" />
            <div className="hero-content">
                <img alt="Kilo Delta Logo" src="/media/logo-full.png" className="logo-hero-banner" />
                <h1>TERMS</h1>
                <h2>AND CONDITIONS</h2>
            </div>
        </section>

        <section className="legal-section">
            <div className="legal-content">
                <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

                <p className="legal-intro">
                    These terms govern your use of this website and any donation you make through it.
                    By using the site or making a donation, you agree to them. Please read the
                    emergency services notice in section 7 in particular.
                </p>

                <h2>1. Who we are</h2>
                <p>
                    Kilo Delta On Call NPO (&ldquo;Kilo Delta On Call&rdquo;, &ldquo;we&rdquo;,
                    &ldquo;us&rdquo;, &ldquo;our&rdquo;) is a registered South African non-profit
                    organisation providing volunteer emergency response support.
                </p>
                <ul>
                    <li><strong>Registered name:</strong> KILO DELTA ON CALL</li>
                    <li><strong>Registration number:</strong> 2025/750743/08</li>
                    <li><strong>Email:</strong> <a href="mailto:info@kilodeltaoncall.org">info@kilodeltaoncall.org</a></li>
                    <li><strong>Telephone:</strong> <a href="tel:0832779727">083 277 9727</a> (John) / <a href="tel:0872655289">0872 655 289</a> (Support)</li>
                    <li><strong>Website:</strong> www.kilodeltaoncall.org</li>
                </ul>

                <h2>2. Using this website</h2>
                <ul>
                    <li>This site exists to share information about our work and to accept donations.</li>
                    <li>We take care to keep the information here accurate and current, but we do not warrant that it is complete or free of errors.</li>
                    <li>You may not use this site unlawfully, attempt to gain unauthorised access to it, or interfere with its operation.</li>
                    <li>The content on this site — text, photographs, the Kilo Delta On Call name and emblem — belongs to us or to our licensors. You may not reproduce it without our permission, other than for personal, non-commercial use.</li>
                    <li>Sponsor and partner logos remain the property of their owners and are shown with their permission.</li>
                    <li>We may link to other websites. We do not control them and are not responsible for their content.</li>
                </ul>

                <h2>3. Donations</h2>
                <ul>
                    <li>Donations are voluntary and are made in South African Rand (ZAR). The minimum is R5.00.</li>
                    <li>You may give once off, or monthly.</li>
                    <li>A monthly donation is charged on the same date each month until you cancel it. You can cancel at any time by emailing <a href="mailto:info@kilodeltaoncall.org">info@kilodeltaoncall.org</a> or through your Payfast account. Cancelling stops future payments; it does not reverse payments already made.</li>
                    <li>Donating does not entitle you to any goods or services, to membership, or to any say in how we operate.</li>
                    <li>We use donations to fund our emergency response work — equipment, fuel, training and running costs. We cannot guarantee that a donation will be applied to a specific item or incident unless we have agreed that with you in writing.</li>
                    <li>If you need a receipt or a tax certificate for your donation, please contact us.</li>
                </ul>

                <h2>4. Payments and security</h2>
                <ul>
                    <li>Payments are processed by Payfast (Pty) Ltd, a South African payment gateway and a PCI DSS Level 1 service provider.</li>
                    <li>When you donate you are taken to Payfast&rsquo;s secure payment page to complete the payment.</li>
                    <li><strong>We never see, receive or store your card details.</strong> They are handled entirely by Payfast.</li>
                    <li>Which payment methods are available is determined by Payfast. Monthly donations are processed by credit card only.</li>
                    <li>Payfast&rsquo;s own terms apply to the payment itself.</li>
                    <li>We are not responsible for failures, delays or errors in Payfast&rsquo;s systems, though we will help you resolve a problem wherever we can.</li>
                </ul>

                <h2>5. Refunds</h2>
                <p>
                    Donations are voluntary and are generally not refundable. If you donated in error —
                    a duplicate payment, the wrong amount, or a payment you did not authorise — please
                    contact us within 30 days at <a href="mailto:info@kilodeltaoncall.org">info@kilodeltaoncall.org</a> and
                    we will refund it.
                </p>
                <p>
                    Monthly donations can be cancelled at any time. A cancellation stops future
                    payments and does not refund payments already made.
                </p>

                <h2>6. Privacy and your personal information</h2>
                <p>
                    This section is our privacy notice under the Protection of Personal Information
                    Act, 2013 (POPIA).
                </p>

                <h3>What we collect</h3>
                <ul>
                    <li>Information you give us when you contact us or donate — your name, email address and telephone number.</li>
                    <li>Details of your donation — the amount, the date, and the payment reference.</li>
                    <li><strong>We do not collect or store your card or banking details.</strong> Those go directly to Payfast.</li>
                </ul>

                <h3>Why we process it</h3>
                <ul>
                    <li>To process your donation and send you a receipt.</li>
                    <li>To keep the financial and donor records we are required to keep as a registered NPO.</li>
                    <li>To reply to you if you have contacted us.</li>
                </ul>

                <h3>Who we share it with</h3>
                <ul>
                    <li>Payfast, to process your payment.</li>
                    <li>Our bank and our auditors, as part of ordinary financial administration.</li>
                    <li>Anyone we are required to share it with by law.</li>
                    <li><strong>We do not sell, rent or trade your personal information.</strong></li>
                </ul>

                <h3>Cookies</h3>
                <p>
                    This website does not use tracking, advertising or analytics cookies. Payfast may
                    set cookies on its own payment pages, which are governed by Payfast&rsquo;s privacy
                    policy rather than this one.
                </p>

                <h3>How long we keep it</h3>
                <p>
                    For as long as we need it for the purpose it was collected, and for as long as
                    the law requires us to keep financial records.
                </p>

                <h3>Your rights</h3>
                <p>
                    You have the right to ask what personal information we hold about you, to have it
                    corrected or deleted, and to object to our processing of it. Email us
                    at <a href="mailto:info@kilodeltaoncall.org">info@kilodeltaoncall.org</a> and we
                    will respond. If you are not satisfied, you may complain to the Information
                    Regulator of South Africa at <a href="https://inforegulator.org.za">inforegulator.org.za</a>.
                </p>

                <h2>7. Emergency services — important</h2>
                <div className="legal-callout">
                    <p>
                        <strong>Kilo Delta On Call is a volunteer-led emergency support organisation.
                        We are not a licensed emergency medical services (EMS) provider.</strong>
                    </p>
                    <p>
                        In an emergency, contact the official emergency services
                        first: <strong>10177</strong> (ambulance and fire), <strong>10111</strong> (police),
                        or <strong>112</strong> from a mobile phone.
                    </p>
                </div>
                <ul>
                    <li>We assist on a best-efforts, volunteer basis. We cannot guarantee that we will be available, or that we will reach you within any particular time.</li>
                    <li>Nothing on this website is medical, safety or legal advice.</li>
                    <li>Our services bridge the gap until professional emergency services arrive. They do not replace them.</li>
                </ul>

                <h2>8. Liability</h2>
                <p>
                    To the fullest extent permitted by law, we are not liable for any indirect or
                    consequential loss arising from your use of this website, and we do not warrant
                    that the site will be available uninterrupted or free of errors.
                </p>
                <p>
                    Nothing in these terms excludes or limits any liability that cannot be excluded
                    or limited under South African law, including under the Consumer Protection Act,
                    2008, or any liability for gross negligence.
                </p>

                <h2>9. Changes to these terms</h2>
                <p>
                    We may update these terms from time to time. The version published on this page
                    at the time you use the site or make a donation is the version that applies.
                </p>

                <h2>10. Governing law</h2>
                <p>
                    These terms are governed by the laws of the Republic of South Africa, and are
                    subject to the jurisdiction of the South African courts.
                </p>

                <h2>11. Contact us</h2>
                <p>
                    Any question about these terms, your personal information, or a donation:
                </p>
                <ul>
                    <li><strong>Email:</strong> <a href="mailto:info@kilodeltaoncall.org">info@kilodeltaoncall.org</a></li>
                    <li><strong>Telephone:</strong> <a href="tel:0832779727">083 277 9727</a> (John)</li>
                    <li><strong>Support line:</strong> <a href="tel:0872655289">0872 655 289</a></li>
                </ul>

                <p className="legal-back">
                    <Link to="/donate">Back to donations</Link>
                </p>
            </div>
        </section>

        <Footer />
    </div>
);

export default Terms;
